<template>
    <div class="mt-6">
        <div class="redesign-table">
            <div class="table-wrapper">
                <div class="row is-header level mb-0">
                    <div class="level-left">
                        <h2 class="level-item">Portal-Zugänge</h2>
                    </div>
                </div>

                <div class="row is-head">
                    <div class="cell is-60px-cell has-text-centered">Avatar</div>
                    <div class="cell is-150px-cell">Vorname</div>
                    <div class="cell is-150px-cell">Nachname</div>
                    <div class="cell">E-Mail</div>
                    <div class="cell is-90px-cell has-text-centered">Farbe</div>
                    <div class="cell is-150px-cell">Status</div>
                    <div class="cell is-150px-cell">Letzter Login</div>
                    <div class="cell is-200px-cell">Einladungslink</div>
                    <div class="cell is-150px-cell has-text-centered">Zugang</div>
                    <div class="cell is-150px-cell has-text-centered" title="Admins (z. B. we-tours-Mitarbeiter) werden im Portal nicht als Zuständige angeboten">Admin</div>
                    <div class="cell is-actions is-100px-cell has-text-centered">Aktionen</div>
                </div>

                <div class="is-zebra">
                    <div class="row" v-for="user in users" :key="user.id">
                        <!-- Same UserImage the customer sees in the portal — the
                             stored avatarColor puts it into initials mode. -->
                        <div class="cell is-60px-cell has-text-centered">
                            <user-image :user="user" small />
                        </div>
                        <div class="cell is-150px-cell">
                            <input
                                class="input is-small"
                                v-model.trim="user.firstName"
                                placeholder="—"
                                @blur="saveField(user, 'firstName')"
                                @keyup.enter="$event.target.blur()"
                            />
                        </div>
                        <div class="cell is-150px-cell">
                            <input
                                class="input is-small"
                                v-model.trim="user.lastName"
                                placeholder="—"
                                @blur="saveField(user, 'lastName')"
                                @keyup.enter="$event.target.blur()"
                            />
                        </div>
                        <div class="cell">{{ user.email }}</div>
                        <div class="cell is-90px-cell has-text-centered">
                            <popover>
                                <button
                                    class="portal-color-swatch"
                                    type="button"
                                    :style="{ background: user.avatarColor }"
                                    :title="user.avatarColorOverride ? 'Farbe gewählt' : 'Farbe automatisch'"
                                ></button>
                                <template slot="popover">
                                    <div class="portal-color-palette">
                                        <button
                                            v-for="color in avatarColors"
                                            :key="color"
                                            type="button"
                                            :style="{ background: color }"
                                            :class="{ 'is-active': user.avatarColor === color }"
                                            @click.prevent="saveField(user, 'avatarColor', color)"
                                        ></button>
                                    </div>
                                    <a class="is-size-7" @click.prevent="saveField(user, 'avatarColor', '')">
                                        automatisch
                                    </a>
                                </template>
                            </popover>
                        </div>
                        <div class="cell is-150px-cell">{{ statusLabel(user) }}</div>
                        <div class="cell is-150px-cell">{{ user.lastLoginAt || '—' }}</div>
                        <div class="cell is-200px-cell">
                            <button
                                v-if="user.invitePending"
                                class="button is-small"
                                title="Link zum Passwort-Setzen in die Zwischenablage kopieren"
                                @click.prevent="copyInviteLink(user)"
                            ><i class="material-icons">content_copy</i>&nbsp;Link kopieren</button>
                            <span v-else>—</span>
                        </div>
                        <div class="cell is-150px-cell has-text-centered">
                            <toggle-switch
                                :value="user.isActive"
                                btn-class="is-small"
                                @input="value => setActive(user, value)"
                            >
                                <option :value="true">aktiv</option>
                                <option :value="false">gesperrt</option>
                            </toggle-switch>
                        </div>
                        <div class="cell is-150px-cell has-text-centered">
                            <toggle-switch
                                :value="!!user.isAdmin"
                                btn-class="is-small"
                                @input="value => setAdmin(user, value)"
                            >
                                <option :value="true">Admin</option>
                                <option :value="false">Kunde</option>
                            </toggle-switch>
                        </div>
                        <div class="cell is-actions is-100px-cell has-text-centered">
                            <button
                                class="btn-edit"
                                :title="user.invitePending ? 'Einladung erneut senden (neuer Link)' : 'Neuen Passwort-Link senden'"
                                :disabled="resendingId === user.id"
                                @click.prevent="resendInvite(user)"
                            ><i class="material-icons">send</i></button>
                            <button
                                class="btn-delete"
                                title="Portal-Zugang löschen"
                                @click.prevent="deleteUser(user)"
                            ><i class="material-icons">delete</i></button>
                        </div>
                    </div>

                    <div class="row" v-if="!isLoading && users.length === 0">
                        <div class="cell">Noch keine Portal-Zugänge für diesen Kunden.</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="level mt-3">
            <div class="level-left">
                <!-- Optional name at invite time, so the new login starts with real
                     initials on its avatar instead of two letters of the email. -->
                <div class="level-item control">
                    <input
                        class="input"
                        style="width: 140px"
                        type="text"
                        placeholder="Vorname"
                        v-model.trim="inviteFirstName"
                        @keyup.enter="invite"
                    />
                </div>
                <div class="level-item control">
                    <input
                        class="input"
                        style="width: 140px"
                        type="text"
                        placeholder="Nachname"
                        v-model.trim="inviteLastName"
                        @keyup.enter="invite"
                    />
                </div>
                <div class="level-item control">
                    <input
                        class="input"
                        style="width: 320px"
                        type="email"
                        placeholder="email@kunde.ch"
                        v-model.trim="inviteEmail"
                        @keyup.enter="invite"
                    />
                </div>
                <button
                    class="button is-primary level-item"
                    :class="{'is-loading': isInviting}"
                    :disabled="!inviteEmail || isInviting"
                    @click.prevent="invite"
                >PortalUser einladen</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Mixin from '../tab';
import { ToggleSwitch } from '@components/form';
import Popover from '@components/Popover';
import UserImage from '@components/UserImage';
import { copyText } from '@utilities/functions';
import { AVATAR_COLORS } from '@utilities/avatarColors';
import { notifySuccess, notifyError } from '@components/Notification';

export default {
    mixins: [Mixin],

    data: function () {
        return {
            users: [],
            inviteEmail: '',
            inviteFirstName: '',
            inviteLastName: '',
            isLoading: false,
            isInviting: false,
            resendingId: null,
            // Fixed palette rather than <input type="color">: the backend validates
            // against the same list, so a portal avatar never turns neon.
            avatarColors: AVATAR_COLORS,
        };
    },

    methods: {
        fetchUsers() {
            if (!this.item || !this.item.id) {
                return;
            }
            this.isLoading = true;
            axios.get(`/api/portal-users/${this.item.id}`)
                .then((response) => {
                    this.users = Array.isArray(response.data) ? response.data : [];
                })
                .catch(() => notifyError('Portal-Zugänge konnten nicht geladen werden'))
                .then(() => { this.isLoading = false; });
        },

        statusLabel(user) {
            if (user.invitePending) {
                return user.inviteExpiresAt ? `eingeladen (bis ${user.inviteExpiresAt})` : 'eingeladen';
            }
            return user.isActive ? 'aktiv' : 'gesperrt';
        },

        inviteLink(user) {
            return `${window.location.origin}/client-portal/set-password?token=${user.inviteToken}`;
        },

        copyInviteLink(user) {
            copyText(this.inviteLink(user));
            notifySuccess('Einladungslink kopiert');
        },

        invite() {
            if (!this.inviteEmail || this.isInviting) {
                return;
            }
            this.isInviting = true;
            axios.post('/api/client-portal/invite', {
                email: this.inviteEmail,
                clientId: this.item.id,
                firstName: this.inviteFirstName,
                lastName: this.inviteLastName,
            })
                .then((response) => {
                    if (response.data && response.data.mailSent) {
                        notifySuccess('Einladung per E-Mail verschickt');
                    } else {
                        notifyError('Einladung erstellt, aber E-Mail-Versand fehlgeschlagen — bitte Link über die Tabelle kopieren und manuell senden');
                    }
                    this.inviteEmail = '';
                    this.inviteFirstName = '';
                    this.inviteLastName = '';
                    this.fetchUsers();
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.error
                        ? error.response.data.error
                        : 'Einladung fehlgeschlagen';
                    notifyError(message);
                })
                .then(() => { this.isInviting = false; });
        },

        resendInvite(user) {
            this.resendingId = user.id;
            axios.post(`/api/portal-users/${user.id}/resend-invite`)
                .then((response) => {
                    if (response.data && response.data.mailSent) {
                        notifySuccess('Neuer Link per E-Mail verschickt');
                    } else {
                        notifyError('Neuer Link erstellt, aber E-Mail-Versand fehlgeschlagen — bitte Link über die Tabelle kopieren');
                    }
                    this.fetchUsers();
                })
                .catch(() => notifyError('Link konnte nicht erstellt werden'))
                .then(() => { this.resendingId = null; });
        },

        deleteUser(user) {
            if (!confirm(`Portal-Zugang ${user.email} wirklich löschen?`)) {
                return;
            }
            axios.delete(`/api/portal-users/${user.id}`)
                .then(() => {
                    notifySuccess('Portal-Zugang gelöscht');
                    this.fetchUsers();
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.error
                        ? error.response.data.error
                        : 'Löschen fehlgeschlagen';
                    notifyError(message);
                });
        },

        setActive(user, value) {
            this.saveField(user, 'isActive', value, value ? 'Zugang aktiviert' : 'Zugang gesperrt');
        },

        // Admins keep their login but drop out of the portal's assignee lists.
        setAdmin(user, value) {
            this.saveField(user, 'isAdmin', value, value ? 'Als Admin markiert' : 'Admin-Markierung entfernt');
        },

        /**
         * Single-field PUT against the admin whitelist. `value` defaults to whatever
         * the row already holds, so an inline input can just @blur into it. The
         * response is merged back so derived fields (fullName, initials, the resolved
         * avatarColor) refresh without another request.
         */
        saveField(user, key, value = undefined, message = 'Gespeichert') {
            const payload = { [key]: value === undefined ? user[key] : value };

            return axios.put(`/api/portal-users/${user.id}`, payload)
                .then((response) => {
                    Object.assign(user, response.data);
                    notifySuccess(message);
                })
                .catch(() => notifyError('Speichern fehlgeschlagen'));
        },
    },

    mounted: function () {
        this.fetchUsers();
    },

    activated: function () {
        this.fetchUsers();
    },

    components: {
        ToggleSwitch,
        Popover,
        UserImage,
    },
};
</script>
